import { useParams } from "react-router-dom";
import { Box, Button, Grid, Stack, Zoom } from "@mui/material";
import {
  COLOR_BLACK,
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
  COLOR_WHITE,
} from "../../../../data/colors/Colors";
import {
  Attachment,
  CheckCircle,
  CloudUploadRounded,
  Delete,
  EditRounded,
  Save,
} from "@mui/icons-material";
import { CustomImageView } from "../../../component/CustomImageView";
import useWindowDimensions from "../../../../core/utils/UseWindowDimensions";
import {
  STRINGS_ARE_YOU_SURE_YOU_WANT_TO_DELETE_THE_STORE,
  STRINGS_CREATE,
  STRINGS_DELETE,
  STRINGS_DELETE_STORE,
  STRINGS_EDIT_TITLE,
  STRINGS_SAVE,
  STRINGS_TITLE,
} from "../../../../data/strings/Strings";
import { useEffect, useRef, useState } from "react";
import { CustomTextField } from "../../../component/CustomTextField";
import { CustomTextView } from "../../../component/CustomTextView";
import { HeaderView } from "../../../component/HeaderView";
import { CustomTitledView } from "../../../component/CustomTitledView";
import { ROUTE_NEW } from "../../../../data/constant/RouteConstants";
import { StoreDetailsViewModel } from "./StoreDetailsViewModel";
import { FilePicker } from "../../../component/FilePicker";
import {
  provideCreateStoreUseCase,
  provideDeleteStoreUseCase,
  provideGetPerProductReportUseCase,
  provideGetStoreUseCase,
  provideUpdateStoreUseCase,
  provideUploadFileUseCase,
} from "../../../di/UseCasesModule";
import { CustomAlertDialog } from "../../../component/CustomAlertDialog";
import { hasEnglishLetters } from "../../../../core/utils/Utils";
import Marquee from "react-fast-marquee";
import { LoadingView } from "../../../component/LoadingView";
import { ChooseStartEndDateView } from "../../../component/ChooseStartEndDateView";

export function StoreDetailsView() {
  const { id } = useParams();
  const viewModel = StoreDetailsViewModel(
    provideGetStoreUseCase(),
    provideCreateStoreUseCase(),
    provideDeleteStoreUseCase(),
    provideUpdateStoreUseCase(),
    provideUploadFileUseCase(),
    provideGetPerProductReportUseCase()
  );
  const { width } = useWindowDimensions();
  const isNewPage = id === ROUTE_NEW;
  const [nameEditable, setNameEditable] = useState(isNewPage);
  const [branchNameEditable, setBranchNameEditable] = useState(isNewPage);
  const [branchAddressEditable, setBranchAddressEditable] = useState(isNewPage);
  const [branchNumberEditable, setBranchNumberEditable] = useState(isNewPage);
  const [phoneNumberEditable, setPhoneNumberEditable] = useState(isNewPage);
  const [contactPersonEditable, setContactPersonEditable] = useState(isNewPage);
  const [showDeleteAlertDialog, setShowDeleteAlertDialog] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (!isNewPage && id) {
      viewModel.fetchStore(id);
    }
  }, []);

  const ref = useRef(null);
  const [imageWidth, setImageWidth] = useState();
  useEffect(() => {
    setImageWidth(ref.current ? (ref.current as any).offsetWidth : 0);
  });

  return (
    <Box>
      <CustomAlertDialog
        title={STRINGS_DELETE_STORE}
        description={STRINGS_ARE_YOU_SURE_YOU_WANT_TO_DELETE_THE_STORE}
        open={showDeleteAlertDialog}
        handleNegativeButtonClick={() => setShowDeleteAlertDialog(false)}
        handlePositiveButtonClick={() => {
          viewModel.deleteStore();
          setShowDeleteAlertDialog(false);
        }}
      />
      <Stack>
        {viewModel.isLoading ? (
          <LoadingView />
        ) : (
          <>
            <Stack>
              <HeaderView
                isLayoutFullWidthInSmallerScreens={!isNewPage}
                title={"Store"}
                alignItems="start"
                titleLayout={
                  <>
                    {viewModel.store?._id && (
                      <Marquee style={{ width: 100 }} delay={2} pauseOnHover>
                        <CustomTextView
                          text={`#${viewModel.store._id}`}
                          fontWeight="300"
                          variant="h6"
                          textColor={COLOR_LIGHT_GRAY}
                        />
                      </Marquee>
                    )}
                  </>
                }
                layout={
                  <Stack
                    alignItems={"center"}
                    justifyContent={!isNewPage && width < 900 ? "start" : "end"}
                    spacing={1}
                    width={"100%"}
                  >
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={
                        !isNewPage && width < 900 ? "start" : "end"
                      }
                      spacing={1}
                      width={"100%"}
                    >
                      {!isNewPage && (
                        <Button
                          onClick={() => setShowDeleteAlertDialog(true)}
                          variant="outlined"
                          fullWidth
                          sx={{
                            borderRadius: 22,
                            borderColor: "red",
                            "&:hover": {
                              borderColor: "red",
                            },
                          }}
                        >
                          <Delete fontSize="small" sx={{ color: "red" }} />
                          <CustomTextView
                            text={STRINGS_DELETE}
                            fontWeight="bold"
                            textColor={"red"}
                            fontSize={16}
                            props={{ ml: 1, textTransform: "none" }}
                          />
                        </Button>
                      )}
                      <Button
                        onClick={
                          isNewPage
                            ? viewModel.createStore
                            : viewModel.saveStore
                        }
                        variant="outlined"
                        fullWidth={!isNewPage}
                        sx={{
                          borderRadius: 22,
                          borderColor: COLOR_BLACK,
                          "&:hover": {
                            borderColor: COLOR_BLACK,
                          },
                        }}
                      >
                        <Save fontSize="small" sx={{ color: COLOR_BLACK }} />
                        <CustomTextView
                          text={isNewPage ? STRINGS_CREATE : STRINGS_SAVE}
                          fontWeight="bold"
                          fontSize={16}
                          props={{ ml: 1, textTransform: "none" }}
                          textColor={COLOR_BLACK}
                        />
                      </Button>
                    </Stack>
                  </Stack>
                }
              />

              {!isNewPage && (
                <ChooseStartEndDateView
                  title="Download this Store Report"
                  onClicked={(startDate, endDate) => {
                    viewModel.setCustomDate(startDate, endDate);
                  }}
                />
              )}
              <Box p={3}>
                {/* <TitleView title={"Details"} size="h5" /> */}
                <Grid container spacing={2} mt={2}>
                  <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 8 }}>
                    <Zoom in={viewModel.store != undefined}>
                      <Box height={400} ref={ref} position={"relative"}>
                        {viewModel.store?.logo &&
                          viewModel.store.logo.length > 0 &&
                          imageLoaded && (
                            <Box position={"absolute"} zIndex={3}>
                              <Stack
                                width={"100%"}
                                direction={"row"}
                                alignItems={"center"}
                                justifyContent={"space-between"}
                                padding={2}
                                zIndex={2}
                              >
                                <Button
                                  sx={{
                                    border: "solid 1px gray",
                                    borderRadius: 12,
                                    p: 1,
                                  }}
                                >
                                  <FilePicker
                                    handleFilePicked={viewModel.setLogo}
                                    layout={
                                      <Stack
                                        direction={"row"}
                                        alignItems={"center"}
                                        spacing={1}
                                      >
                                        <Attachment
                                          fontSize="small"
                                          sx={{ color: COLOR_LIGHT_GRAY }}
                                        />
                                      </Stack>
                                    }
                                    acceptType="image/*"
                                  />
                                </Button>
                              </Stack>
                            </Box>
                          )}

                        <Box
                          position={"absolute"}
                          zIndex={2}
                          width={imageWidth}
                          alignItems={"center"}
                          justifyContent={"center"}
                          display={"flex"}
                        >
                          {viewModel.store?.logo &&
                          viewModel.store.logo.length > 0 ? (
                            <Stack width={"100%"}>
                              <CustomImageView
                                width={"100%"}
                                height={400}
                                onLoaded={() => {
                                  setImageLoaded(true);
                                }}
                                src={viewModel.store.logo}
                                scale="contain"
                                props={{
                                  borderRadius: 8,
                                  background: "transparent",
                                  border: imageLoaded
                                    ? `solid 1px ${COLOR_LIGHT_GRAY}`
                                    : undefined,
                                }}
                              />
                            </Stack>
                          ) : (
                            <Box
                              sx={{
                                width: "100%",
                                height: 400,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "solid 1px " + COLOR_LIGHT_GRAY,
                                borderRadius: 8,
                              }}
                            >
                              <FilePicker
                                handleFilePicked={viewModel.setLogo}
                                layout={
                                  <CloudUploadRounded
                                    sx={{
                                      color: COLOR_MAIN,
                                      width: 200,
                                      height: 200,
                                    }}
                                  />
                                }
                                acceptType="image/*"
                              />
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Zoom>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 4 }}>
                    <CustomTitledView
                      title={"Name"}
                      editIcon={
                        nameEditable ? (
                          <CheckCircle
                            fontSize="medium"
                            sx={{ color: COLOR_BLACK }}
                          />
                        ) : (
                          <EditRounded
                            fontSize="medium"
                            sx={{ color: COLOR_BLACK }}
                          />
                        )
                      }
                      onEditButtonClicked={() => {
                        if (
                          viewModel.store?.name &&
                          viewModel.store.name.length === 0
                        )
                          return;
                        setNameEditable(!nameEditable);
                      }}
                      layout={
                        <Stack>
                          {nameEditable ? (
                            <CustomTextField
                              value={viewModel.store?.name}
                              fontSize={30}
                              textColor={COLOR_BLACK}
                              fontWeight="bold"
                              multiLine
                              backgroundColor="transparent"
                              placeholder={"Edit Name"}
                              variant="standard"
                              onTextChange={viewModel.setName}
                              sxProps={{
                                p: 2,
                                direction: hasEnglishLetters(
                                  viewModel.store?.name ?? ""
                                )
                                  ? "ltr"
                                  : "rtl",
                              }}
                            />
                          ) : (
                            <CustomTextView
                              text={viewModel.store?.name}
                              fontWeight="bold"
                              fontSize={30}
                              textColor={COLOR_BLACK}
                              props={{
                                p: 2,
                                direction: hasEnglishLetters(
                                  viewModel.store?.name ?? ""
                                )
                                  ? "ltr"
                                  : "rtl",
                              }}
                            />
                          )}
                        </Stack>
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
                    <CustomTitledView
                      title={"Branch Name"}
                      editIcon={
                        branchNameEditable ? (
                          <CheckCircle
                            fontSize="medium"
                            sx={{ color: COLOR_BLACK }}
                          />
                        ) : (
                          <EditRounded
                            fontSize="medium"
                            sx={{ color: COLOR_BLACK }}
                          />
                        )
                      }
                      onEditButtonClicked={() => {
                        if (
                          viewModel.store?.branchName &&
                          viewModel.store.branchName.length === 0
                        )
                          return;
                        setBranchNameEditable(!branchNameEditable);
                      }}
                      layout={
                        <Stack>
                          {branchNameEditable ? (
                            <CustomTextField
                              value={viewModel.store?.branchName}
                              fontSize={30}
                              textColor={COLOR_BLACK}
                              fontWeight="bold"
                              multiLine
                              backgroundColor="transparent"
                              placeholder={"Edit Branch Name"}
                              variant="standard"
                              onTextChange={viewModel.setBranchName}
                              sxProps={{
                                p: 2,
                                direction: hasEnglishLetters(
                                  viewModel.store?.branchName ?? ""
                                )
                                  ? "ltr"
                                  : "rtl",
                              }}
                            />
                          ) : (
                            <CustomTextView
                              text={viewModel.store?.branchName}
                              fontWeight="bold"
                              fontSize={30}
                              textColor={COLOR_BLACK}
                              props={{
                                p: 2,
                                direction: hasEnglishLetters(
                                  viewModel.store?.branchName ?? ""
                                )
                                  ? "ltr"
                                  : "rtl",
                              }}
                            />
                          )}
                        </Stack>
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
                    <CustomTitledView
                      title={"Branch Address"}
                      editIcon={
                        branchAddressEditable ? (
                          <CheckCircle
                            fontSize="medium"
                            sx={{ color: COLOR_BLACK }}
                          />
                        ) : (
                          <EditRounded
                            fontSize="medium"
                            sx={{ color: COLOR_BLACK }}
                          />
                        )
                      }
                      onEditButtonClicked={() => {
                        if (
                          viewModel.store?.branchAddress &&
                          viewModel.store.branchAddress.length === 0
                        )
                          return;
                        setBranchAddressEditable(!branchAddressEditable);
                      }}
                      layout={
                        <Stack>
                          {branchAddressEditable ? (
                            <CustomTextField
                              value={viewModel.store?.branchAddress}
                              fontSize={30}
                              textColor={COLOR_BLACK}
                              fontWeight="bold"
                              multiLine
                              backgroundColor="transparent"
                              placeholder={"Edit Branch Address"}
                              variant="standard"
                              onTextChange={viewModel.setBranchAddress}
                              sxProps={{
                                p: 2,
                                direction: hasEnglishLetters(
                                  viewModel.store?.branchAddress ?? ""
                                )
                                  ? "ltr"
                                  : "rtl",
                              }}
                            />
                          ) : (
                            <CustomTextView
                              text={viewModel.store?.branchAddress}
                              fontWeight="bold"
                              fontSize={30}
                              textColor={COLOR_BLACK}
                              props={{
                                p: 2,
                                direction: hasEnglishLetters(
                                  viewModel.store?.branchAddress ?? ""
                                )
                                  ? "ltr"
                                  : "rtl",
                              }}
                            />
                          )}
                        </Stack>
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 4 }}>
                    <CustomTitledView
                      title={"Branch Number"}
                      editIcon={
                        branchNumberEditable ? (
                          <CheckCircle
                            fontSize="medium"
                            sx={{ color: COLOR_BLACK }}
                          />
                        ) : (
                          <EditRounded
                            fontSize="medium"
                            sx={{ color: COLOR_BLACK }}
                          />
                        )
                      }
                      onEditButtonClicked={() => {
                        if (
                          viewModel.store?.branchNumber &&
                          viewModel.store.branchNumber.length === 0
                        )
                          return;
                        setBranchNumberEditable(!branchNumberEditable);
                      }}
                      layout={
                        <Stack>
                          {branchNumberEditable ? (
                            <CustomTextField
                              value={viewModel.store?.branchNumber}
                              fontSize={30}
                              textColor={COLOR_BLACK}
                              fontWeight="bold"
                              multiLine
                              backgroundColor="transparent"
                              placeholder={"Edit Branch Number"}
                              variant="standard"
                              onTextChange={viewModel.setBranchNumber}
                              sxProps={{
                                p: 2,
                                direction: hasEnglishLetters(
                                  viewModel.store?.branchNumber ?? ""
                                )
                                  ? "ltr"
                                  : "rtl",
                              }}
                            />
                          ) : (
                            <CustomTextView
                              text={viewModel.store?.branchNumber}
                              fontWeight="bold"
                              fontSize={30}
                              textColor={COLOR_BLACK}
                              props={{
                                p: 2,
                                direction: hasEnglishLetters(
                                  viewModel.store?.branchNumber ?? ""
                                )
                                  ? "ltr"
                                  : "rtl",
                              }}
                            />
                          )}
                        </Stack>
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 4 }}>
                    <CustomTitledView
                      title={"Phone Number"}
                      editIcon={
                        phoneNumberEditable ? (
                          <CheckCircle
                            fontSize="medium"
                            sx={{ color: COLOR_BLACK }}
                          />
                        ) : (
                          <EditRounded
                            fontSize="medium"
                            sx={{ color: COLOR_BLACK }}
                          />
                        )
                      }
                      onEditButtonClicked={() => {
                        if (
                          viewModel.store?.phoneNumber &&
                          viewModel.store.phoneNumber.length === 0
                        )
                          return;
                        setPhoneNumberEditable(!phoneNumberEditable);
                      }}
                      layout={
                        <Stack>
                          {phoneNumberEditable ? (
                            <CustomTextField
                              value={viewModel.store?.phoneNumber}
                              fontSize={30}
                              textColor={COLOR_BLACK}
                              fontWeight="bold"
                              multiLine
                              backgroundColor="transparent"
                              placeholder={"Edit Phone Number"}
                              variant="standard"
                              onTextChange={viewModel.setPhoneNumber}
                              sxProps={{
                                p: 2,
                              }}
                            />
                          ) : (
                            <CustomTextView
                              text={viewModel.store?.phoneNumber}
                              fontWeight="bold"
                              fontSize={30}
                              textColor={COLOR_BLACK}
                              props={{
                                p: 2,
                              }}
                            />
                          )}
                        </Stack>
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 4 }}>
                    <CustomTitledView
                      title={"Contact Person"}
                      editIcon={
                        contactPersonEditable ? (
                          <CheckCircle
                            fontSize="medium"
                            sx={{ color: COLOR_BLACK }}
                          />
                        ) : (
                          <EditRounded
                            fontSize="medium"
                            sx={{ color: COLOR_BLACK }}
                          />
                        )
                      }
                      onEditButtonClicked={() => {
                        if (
                          viewModel.store?.name &&
                          viewModel.store.name.length === 0
                        )
                          return;
                        setContactPersonEditable(!contactPersonEditable);
                      }}
                      layout={
                        <Stack>
                          {contactPersonEditable ? (
                            <CustomTextField
                              value={viewModel.store?.contactPerson}
                              fontSize={30}
                              textColor={COLOR_BLACK}
                              fontWeight="bold"
                              multiLine
                              backgroundColor="transparent"
                              placeholder={"Edit Contact Person"}
                              variant="standard"
                              onTextChange={viewModel.setContactPerson}
                              sxProps={{
                                p: 2,
                                direction: hasEnglishLetters(
                                  viewModel.store?.contactPerson ?? ""
                                )
                                  ? "ltr"
                                  : "rtl",
                              }}
                            />
                          ) : (
                            <CustomTextView
                              text={viewModel.store?.contactPerson}
                              fontWeight="bold"
                              fontSize={30}
                              textColor={COLOR_BLACK}
                              props={{
                                p: 2,
                                direction: hasEnglishLetters(
                                  viewModel.store?.contactPerson ?? ""
                                )
                                  ? "ltr"
                                  : "rtl",
                              }}
                            />
                          )}
                        </Stack>
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
}
